<template>
  <div class="port-dashboard">
    <!-- Header Area -->
    <header class="port-header md-layout md-alignment-center-space-between">
      <div>
        <h2 class="md-title">{{ portName }}</h2>
        <p class="md-subhead text-muted">{{ portDescription }}</p>
      </div>
      <div class="header-actions" v-if="isLoading">
        <md-progress-spinner md-mode="indeterminate" :md-diameter="24" :md-stroke="2"></md-progress-spinner>
      </div>
    </header>

    <!-- Key Metrics Grid -->
    <section class="metrics-grid">
      <div v-for="metric in metricCards" :key="metric.label" :class="['metric-card', metric.variant]">
        <div class="metric-icon">
          <md-icon>{{ metric.icon }}</md-icon>
        </div>
        <div class="metric-content">
          <div class="metric-value">{{ metric.formattedValue }}</div>
          <div class="metric-label">{{ metric.label }}</div>
        </div>
      </div>
    </section>

    <!-- Section: Performance & Distribution -->
    <section class="md-layout md-gutter">
      <div class="md-layout-item md-size-60 md-small-size-100">
        <md-card class="dashboard-card">
          <md-card-header>
            <div class="md-title">Guidances by Department</div>
          </md-card-header>
          <md-card-content class="chart-container">
            <canvas ref="departmentChartCanvas"></canvas>
          </md-card-content>
        </md-card>
      </div>

      <div class="md-layout-item md-size-40 md-small-size-100">
        <md-card class="dashboard-card">
          <md-card-header>
            <div class="md-title">Labourer Distribution</div>
          </md-card-header>
          <md-card-content class="chart-container">
            <canvas ref="labourerChartCanvas"></canvas>
          </md-card-content>
        </md-card>
      </div>
    </section>

    <!-- Department Performance Table -->
    <section class="md-layout md-gutter mt-4">
    <md-card class="dashboard-card">
      <md-card-header>
        <div class="md-title">Department Performance</div>
      </md-card-header>
      <md-card-content>
        <md-table v-model="departments" md-sort="name" md-sort-order="asc" md-card>
          <md-table-row slot="md-table-row" slot-scope="{ item }">
            <md-table-cell md-label="Department" md-sort-by="name">{{ item.name }}</md-table-cell>
            <md-table-cell md-label="Amount" md-numeric>{{ formatCurrency(item.total_cost) }}</md-table-cell>
            <md-table-cell md-label="Guidances" md-numeric>{{ item.total_guidances }}</md-table-cell>
            <md-table-cell md-label="Active" md-numeric>{{ item.active_guidances }}</md-table-cell>
            <md-table-cell md-label="Labourers" md-numeric>{{ item.total_utilized_labourers }}/{{ item.total_labourers }}</md-table-cell>
            <md-table-cell md-label="Utilization">
              <div class="progress-cell">
                <md-progress-bar :md-value="item.percent_utilized_labourers" md-mode="determinate"></md-progress-bar>
                <span>{{ item.percent_utilized_labourers }}%</span>
              </div>
            </md-table-cell>
            <md-table-cell md-label="Status">
              <badge :type="getStatusBadge(item.status)">{{ item.status }}</badge>
            </md-table-cell>
            <md-table-cell md-label="Actions">
              <md-button class="md-icon-button md-primary" @click="viewDepartment(item.id)">
                <md-icon>visibility</md-icon>
              </md-button>
            </md-table-cell>
          </md-table-row>
        </md-table>
      </md-card-content>
    </md-card>
    </section>

    <!-- Vendor Analytics Section -->
    <section class="md-layout md-gutter mt-4">
      <div class="md-layout-item md-size-60 md-small-size-100">
        <md-card class="dashboard-card">
          <md-card-header>
            <div class="md-title">Vendor Analysis</div>
          </md-card-header>
          <md-card-content>
            <md-table v-model="vendors" md-card>
              <md-table-row slot="md-table-row" slot-scope="{ item }">
                <md-table-cell md-label="Vendor">{{ item.name }}</md-table-cell>
                <md-table-cell md-label="Labourer" md-numeric>{{ formatNumber(item.total_labourers) }}</md-table-cell>
                <md-table-cell md-label="ManHour" md-numeric>{{ formatNumber(item.total_man_hours) }}</md-table-cell>
                <md-table-cell md-label="Avg Rate" md-numeric>{{ formatCurrency(item.avg_hourly_rate) }}</md-table-cell>
                <md-table-cell md-label="Amount" md-numeric>{{ formatCurrency(item.total_cost) }}</md-table-cell>
                <md-table-cell md-label="% Cost">
                  <div class="progress-cell">
                    <md-progress-bar :md-value="item.percent_of_total_cost" md-mode="determinate"></md-progress-bar>
                    <span>{{ item.percent_of_total_cost }}%</span>
                  </div>
                </md-table-cell>
                <md-table-cell md-label="Actions">
                  <md-button class="md-icon-button md-primary" @click="viewVendor(item.id)">
                    <md-icon>visibility</md-icon>
                  </md-button>
                </md-table-cell>
              </md-table-row>
            </md-table>
          </md-card-content>
        </md-card>
      </div>

      <div class="md-layout-item md-size-40 md-small-size-100">
        <md-card class="dashboard-card">
          <md-card-header>
            <div class="md-title">Vendor Cost Distribution</div>
          </md-card-header>
          <md-card-content class="chart-container">
            <canvas ref="vendorChartCanvas"></canvas>
          </md-card-content>
        </md-card>
      </div>
    </section>

    <!-- Top Activities -->
    <section class="md-layout md-gutter mt-4">
    <md-card class="dashboard-card mt-4">
      <md-card-header>
        <div class="md-title">Top 20 Activities</div>
      </md-card-header>
      <md-card-content>
        <md-table v-model="activities" md-card>
          <md-table-row slot="md-table-row" slot-scope="{ item }">
            <md-table-cell md-label="Activity">{{ item.name }}</md-table-cell>
            <md-table-cell md-label="Work Points" md-numeric>{{ formatNumber(item.guidance_count) }}</md-table-cell>
            <md-table-cell md-label="Labourers" md-numeric>{{ formatNumber(item.total_labourers) }}</md-table-cell>
            <md-table-cell md-label="Man Hours" md-numeric>{{ formatNumber(item.total_man_hours) }}</md-table-cell>
            <md-table-cell md-label="Hour Rate" md-numeric>{{ formatCurrency(item.avg_hourly_rate) }}</md-table-cell>
            <md-table-cell md-label="Total Cost" md-numeric>{{ formatCurrency(item.total_cost) }}</md-table-cell>
            <md-table-cell md-label="Actions">
              <md-button class="md-icon-button md-primary" @click="viewActivity(item.id)">
                <md-icon>visibility</md-icon>
              </md-button>
            </md-table-cell>
          </md-table-row>
        </md-table>
      </md-card-content>
    </md-card>
    </section>
    <!-- Cost Tracking Timeline -->
    <md-card class="dashboard-card mt-4">
      <md-card-header>
        <div class="md-title">Cost Track History</div>
      </md-card-header>
      <md-card-content class="chart-container-lg">
        <canvas ref="comparisonChartCanvas"></canvas>
      </md-card-content>
    </md-card>

    <!-- Audit / Stream Log -->
    <md-card class="dashboard-card mt-4">
      <md-card-header>
        <div class="md-title">Recent Event Stream</div>
      </md-card-header>
      <md-card-content>
        <div class="activity-list" v-if="recentActivities.length > 0">
          <div v-for="act in recentActivities" :key="act.id" class="activity-item">
            <div :class="['activity-icon', act.type]">
              <md-icon>{{ getActivityIcon(act.type) }}</md-icon>
            </div>
            <div class="activity-details">
              <div class="activity-title">{{ act.title }}</div>
              <div class="activity-description">{{ act.description }}</div>
              <div class="activity-time">{{ formatRelativeTime(act.created_at) }}</div>
            </div>
            <div class="activity-department">
              <badge type="info">{{ act.department }}</badge>
            </div>
          </div>
        </div>
        <div v-else class="empty-state">
          <md-icon class="md-size-2x">info</md-icon>
          <p>No recent activity observed within this date scope.</p>
        </div>
      </md-card-content>
    </md-card>
  </div>
</template>

<script>
import Chart from 'chart.js/auto';
import Badge from '@/components/Badge.vue';
import { mapGetters } from 'vuex';
import ChartDataLabels from 'chartjs-plugin-datalabels';

Chart.register(ChartDataLabels);

const COLOR_PALETTE = ['#4caf50', '#ff9800', '#2196f3', '#f44336', '#9c27b0', '#00bcd4'];

export default {
  name: 'PortDashboard',
  components: { Badge },
  props: {
    dateRange: {
      type: Object,
      default: () => ({ start: null, end: null })
    }
  },
  data() {
    return {
      portId: null,
      portName: '',
      portDescription: '',
      totalDepartments: 0,
      totalGuidances: 0,
      activeGuidances: 0,
      totalLabourers: 0,
      totalCost: 0,
      departments: [],
      comparisonPeriods: [],
      comparison: {},
      activities: [],
      vendors: [],
      recentActivities: [],
      charts: {
        department: null,
        comparison: null,
        labourer: null,
        vendor: null
      }
    };
  },
  computed: {
    ...mapGetters('dashboard', ['isLoading']),
    me() {
      return this.$store.state.profile?this.$store.state.profile.me : null;
    },
    metricCards() {
      return [
        { label: 'Departments', formattedValue: this.formatNumber(this.totalDepartments), icon: 'account_balance', variant: 'primary' },
        { label: 'Total Guidances', formattedValue: this.formatNumber(this.totalGuidances), icon: 'assignment', variant: 'success' },
        { label: 'Active Guidances', formattedValue: this.formatNumber(this.activeGuidances), icon: 'play_circle', variant: 'warning' },
        { label: 'Total Cost', formattedValue: this.formatCurrency(this.totalCost), icon: 'attach_money', variant: 'info' },
        { label: 'Total Labourers', formattedValue: this.formatNumber(this.totalLabourers), icon: 'people', variant: 'accent' }
      ];
    }
  },
  created() {
    this.fetchPortData();
  },
  watch: {
    me: {
      handler(val) {
        if (!val) return;
        let targetId=null;
        if(val.model && val.model.department) {
          targetId = val.model.department.port_id;
        }else{
          targetId = val.model.port_id;
        }

        if (targetId && targetId !== this.portId) {
          this.portId = targetId;
          this.fetchPortData();
        }
      },
      deep: true,
      immediate: true
    },
    dateRange: {
      handler() {
        this.fetchPortData();
      },
      deep: true
    }
  },
  methods: {
    async fetchPortData() {
      if (!this.portId) return;
      try {
        await this.$store.dispatch('dashboard/fetchPortStats', {
          port_id: this.portId,
          date_range: this.dateRange
        });
        const data = this.$store.getters['dashboard/portStats'];

        if (data) {
          this.portName = data.port_name || 'Port Dashboard';
          this.portDescription = data.port_description || '';
          this.totalDepartments = data.total_departments || 0;
          this.totalGuidances = data.total_guidances || 0;
          this.activeGuidances = data.active_guidances || 0;
          this.totalLabourers = data.total_labourers || 0;
          this.totalCost = data.total_cost || 0;
          this.departments = data.departments || [];
          this.vendors = data.vendors || [];
          this.comparison = data.cost_comparison || {};
          this.comparisonPeriods = this.comparison.periods || [];
          this.activities = data.activities || [];
          this.recentActivities = data.recent_activities || [];

          this.$nextTick(() => this.renderCharts());
        }
      } catch (error) {
        console.error('[PortDashboard] Execution Error:', error);
        this.$store.dispatch('alerts/error', 'Failed to update dashboard analytics');
      }
    },

    renderCharts() {
      this.destroyCharts();
      this.buildDepartmentChart();
      this.buildLabourerChart();
      this.buildVendorChart();
      this.buildComparisonChart();
    },

    destroyCharts() {
      Object.keys(this.charts).forEach((key) => {
        if (this.charts[key]) {
          this.charts[key].destroy();
          this.charts[key] = null;
        }
      });
    },

    buildDepartmentChart() {
      const el = this.$refs.departmentChartCanvas;
      if (!el) return;

      const labels = this.departments.map(d => d.short_name || d.name);
      const dataset = this.departments.map(d => d.total_cost || 0);

      this.charts.department = new Chart(el.getContext('2d'), {
        type: 'bar',
        data: {
          labels,
          datasets: [{ label: 'Cost Allocation', data: dataset, backgroundColor: '#7b1fa2' }]
        },
        options: { responsive: true, maintainAspectRatio: false,
          plugins: {
            // --- Static Data Labels Configuration ---
            datalabels: {
              anchor: 'end',          // Position relative to bar: 'start', 'center', 'end'
              align: 'top',           // Position relative to anchor: 'top', 'bottom', 'left', 'right'
              color: '#333333',       // Text color
              font: {
                weight: 'bold',
                size: 11
              },
              // Custom Formatter (e.g. format currency or numbers)
              formatter: (value) => {
                if (!value) return '0';
                return new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'TZS',
                  maximumFractionDigits: 0
                }).format(value);
              }
            }
          }

        }
      });
    },

    buildLabourerChart() {
      const el = this.$refs.labourerChartCanvas;
      if (!el) return;

      this.charts.labourer = new Chart(el.getContext('2d'), {
        type: 'doughnut',
        data: {
          labels: this.departments.map(d => d.name),
          datasets: [{ data: this.departments.map(d => d.total_labourers || 0), backgroundColor: COLOR_PALETTE }]
        },
        options: { responsive: true, maintainAspectRatio: false,
          plugins: {
            datalabels: {
              color: '#ffffff',
              font: { weight: 'bold', size: 12 },
              formatter: (value, ctx) => {
                // Show percentage or raw count on the slice
                let sum = ctx.dataset.data.reduce((a, b) => a + b, 0);
                let percentage = ((value * 100) / sum).toFixed(1) + "%";
                return value > 0 ? percentage : ''; // Hide zero values
              }
            }
          }
        }
      });
    },

    buildVendorChart() {
      const el = this.$refs.vendorChartCanvas;
      if (!el) return;

      this.charts.vendor = new Chart(el.getContext('2d'), {
        type: 'doughnut',
        data: {
          labels: this.vendors.map(v => v.name),
          datasets: [{ data: this.vendors.map(v => v.total_cost || 0), backgroundColor: COLOR_PALETTE }]
        },
        options: { responsive: true, maintainAspectRatio: false }
      });
    },

    buildComparisonChart() {
      const el = this.$refs.comparisonChartCanvas;
      if (!el) return;

      this.charts.comparison = new Chart(el.getContext('2d'), {
        type: 'line',
        data: {
          labels: this.comparisonPeriods.map(p => p.label),
          datasets: [{
            label: `${this.comparison.granularity || 'Period'} Expense`,
            data: this.comparisonPeriods.map(p => p.total_cost || 0),
            borderColor: '#7b1fa2',
            backgroundColor: 'rgba(123, 31, 162, 0.15)',
            fill: true,
            tension: 0.3
          }]
        },
        options: { responsive: true, maintainAspectRatio: false }
      });
    },

    viewDepartment(id) {
      if (id) this.$router.push({ name: 'Department View', params: { id } });
    },
    viewVendor(id) {
      if (id && id !== 'unassigned') this.$router.push({ name: 'Vendor View', params: { id } });
    },
    viewActivity(id) {
      if (id) this.$router.push({ name: 'Activity View', params: { id } });
    },

    getActivityIcon(type) {
      const iconMap = {
        guidance_created: 'assignment',
        labourer_assigned: 'person_add',
        schedule_generated: 'event',
        check_in: 'login',
        check_out: 'logout',
        status_changed: 'swap_horiz'
      };
      return iconMap[type] || 'notifications';
    },

    getStatusBadge(status) {
      const statusMap = { Active: 'success', Inactive: 'danger', Pending: 'warning', Completed: 'info' };
      return statusMap[status] || 'default';
    },

    formatNumber(val) {
      return val ? new Intl.NumberFormat('en-US').format(val) : '0';
    },

    formatCurrency(val) {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'TZS',
        maximumFractionDigits: 0
      }).format(val || 0);
    },

    formatRelativeTime(dateString) {
      if (!dateString) return '';
      const diff = Math.floor((new Date() - new Date(dateString)) / 1000);
      if (diff < 60) return 'Just now';
      if (diff < 3600) return `${Math.floor(diff / 60)} mins ago`;
      if (diff < 86400) return `${Math.floor(diff / 3600)} hrs ago`;
      return `${Math.floor(diff / 86400)} days ago`;
    }
  },
  beforeDestroy() {
    this.destroyCharts();
  }
};
</script>

<style scoped>
.port-dashboard {
  padding: 16px;
}

.port-header {
  margin-bottom: 20px;
}

.metrics-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
}

.metric-card {
  background: var(--md-theme-default-background, #fff);
  border-radius: 8px;
  padding: 16px;
  display: flex;
  align-items: center;
  box-shadow: 0 2px 8px rgba(0,0,0,0.06);
  border-left: 4px solid #9c27b0;
}

.metric-card.success { border-left-color: #4caf50; }
.metric-card.warning { border-left-color: #ff9800; }
.metric-card.info { border-left-color: #2196f3; }
.metric-card.accent { border-left-color: #e91e63; }

.metric-icon {
  margin-right: 16px;
}

.metric-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1.2;
}

.metric-label {
  font-size: 0.85rem;
  color: #777;
}

.dashboard-card {
  margin-bottom: 0;
  height: 100%;
}

.chart-container {
  position: relative;
  height: 280px;
}

.chart-container-lg {
  position: relative;
  height: 350px;
}

.progress-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.activity-list {
  display: flex;
  flex-direction: column;
}

.activity-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #e0e0e0;
}

.activity-item:last-child {
  border-bottom: none;
}

.activity-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f0f0f0;
}

.activity-details {
  flex: 1;
}

.activity-title {
  font-weight: 600;
  font-size: 0.9rem;
}

.activity-description {
  font-size: 0.8rem;
  color: #666;
}

.activity-time {
  font-size: 0.75rem;
  color: #999;
}

.empty-state {
  text-align: center;
  padding: 32px;
  color: #999;
}

.mt-4 {
  margin-top: 24px;
}
</style>